import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Flame, Share2, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Form of the Week — top 3 in-form players (by goals/assists trend) on the
 * Clubhouse dashboard. Pulls both /api/players and /api/players/in-form
 * once on mount; merges them client-side. One-click "Share to Parents"
 * uses navigator.share() with clipboard fallback (mirrors the pattern
 * already used by PlayerSeasonHighlight).
 */
export const FormOfTheWeekWidget = ({ user }) => {
  const [players, setPlayers] = useState([]);
  const [trends, setTrends] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [playersRes, trendsRes] = await Promise.all([
          axios.get(`${API}/players`),
          axios.get(`${API}/players/in-form`).catch(() => ({ data: { trends: {} } })),
        ]);
        if (cancelled) return;
        setPlayers(playersRes.data || []);
        setTrends(trendsRes.data?.trends || {});
      } catch (_) {
        // silent — widget will render empty state
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const top3 = useMemo(() => {
    return players
      .map((p) => {
        const t = trends[p.id];
        if (!t || !t.in_form) return null;
        const score = (t.goals_delta || 0) * 2 + (t.assists_delta || 0);
        return { ...p, trend: t, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }, [players, trends]);

  const teamSuffix = user?.team_name ? ` — ${user.team_name}` : '';

  const shareText = useMemo(() => {
    if (top3.length === 0) return '';
    const lines = top3.map((p, idx) => {
      const t = p.trend;
      const bits = [];
      if (t.goals_delta > 0) bits.push(`+${t.goals_delta}G`);
      if (t.assists_delta > 0) bits.push(`+${t.assists_delta}A`);
      return `${idx + 1}. ${p.name} (${bits.join(' / ')})`;
    });
    return `🔥 Form of the Week${teamSuffix}\n\n${lines.join('\n')}\n\nKeep it going! 👏`;
  }, [top3, teamSuffix]);

  const handleShare = async () => {
    if (!shareText) return;
    const logShare = () => {
      // Fire-and-forget — failures shouldn't block the share UX
      axios
        .post(`${API}/players/share-events`, {
          reminder_type: 'form_of_week_share',
          metadata: {
            player_ids: top3.map((p) => p.id),
            top_score: top3[0]?.score ?? null,
          },
        })
        .catch(() => {});
    };
    const payload = { title: 'Form of the Week', text: shareText };
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share(payload);
        logShare();
        return;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
        toast.success('Form of the Week copied — paste it into your parent group chat.');
        logShare();
        return;
      }
    } catch (_) {
      // fall through
    }
    window.prompt('Copy this message to share with parents:', shareText);
    logShare();
  };

  return (
    <div className="h-full" data-testid="form-of-the-week">
      <div className="flex flex-row items-center justify-between pb-4">
        <h3 className="text-xl font-semibold font-title flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" strokeWidth={2} />
          Form of the Week
        </h3>
        <Link to="/players">
          <Button
            variant="ghost"
            size="sm"
            style={{ color: user?.menu_color || '#059669' }}
            className="hover:opacity-80"
          >
            View Squad
          </Button>
        </Link>
      </div>
      <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #e5e2dc' }}>
        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Loading…</div>
        ) : top3.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
            <p className="text-sm">No trend data yet</p>
            <p className="text-xs mt-1">Players need 6+ appearances to show form.</p>
          </div>
        ) : (
          <>
            {top3.map((player, index) => {
              const t = player.trend;
              return (
                <div
                  key={player.id}
                  className={`flex items-center gap-3 px-3 py-2.5 ${
                    index < top3.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                  data-testid={`form-row-${player.id}`}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 bg-orange-100 text-orange-700">
                    {index + 1}
                  </div>
                  <Link
                    to={`/players/${player.id}`}
                    className="flex-1 min-w-0 font-medium text-gray-900 truncate hover:underline"
                  >
                    {player.name}
                  </Link>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {t.goals_delta > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        +{t.goals_delta}G
                      </span>
                    )}
                    {t.assists_delta > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        +{t.assists_delta}A
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="px-3 py-2.5 border-t border-gray-100 bg-gray-50">
              <Button
                onClick={handleShare}
                size="sm"
                className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="form-of-the-week-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" strokeWidth={2} />
                Share to Parents
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FormOfTheWeekWidget;
